import { Button, Flexbox, Icon, Input, Spacer, Text } from '@components/common'
import { useStyletron } from 'styletron-react'
import { PAGINATION_NEXT_BUTTON_ID, PAGINATION_PREV_BUTTON_ID } from '@constants'
import { InputChangeEvent } from '@types'

import PaginationStyles from './styles'
var styles = new PaginationStyles()

interface IPagination {
  changePage: (e: React.MouseEvent<HTMLButtonElement>) => void
  changeItemsPerPage?: (e: InputChangeEvent) => void
  goToPage?: (e: InputChangeEvent) => void
  totalItems: number
  maxItemsPerPage?: number
  currentPageNumber: number
  label?: string
  labelStart?: string
  pageSizeValue?: string | number
  pageSizeError?: boolean
  pageSizeErrorMsg?: string
}

const Pagination = ({
  changePage,
  changeItemsPerPage,
  goToPage,
  totalItems,
  maxItemsPerPage,
  currentPageNumber,
  label,
  labelStart,
  pageSizeValue,
  pageSizeError,
  pageSizeErrorMsg
}: IPagination) => {
  const [css] = useStyletron()

  const itemsPerPage = Math.max(1, Number(pageSizeValue ?? maxItemsPerPage ?? 1))
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const handlePageInputChange = (e: InputChangeEvent) => {
    const inputValue = e.target.value
    const pageNumber = parseInt(inputValue)

    if (inputValue === '') {
      return
    }

    const maxPage = totalPages > 0 ? totalPages : 1

    if (isNaN(pageNumber) || pageNumber < 1) {
      e.target.value = '1'
    } else if (pageNumber > maxPage) {
      e.target.value = maxPage.toString()
    }

    if (goToPage) {
      goToPage(e)
    }
  }

  const handlePageInputBlur = (e: InputChangeEvent) => {
    const inputValue = e.target.value
    const maxPage = totalPages > 0 ? totalPages : 1

    if (inputValue === '' || inputValue === '0') {
      const validCurrentPage = currentPageNumber > maxPage ? maxPage : currentPageNumber
      e.target.value = validCurrentPage.toString()
      if (goToPage) {
        goToPage(e)
      }
    }
  }

  const handleItemsPerPageChange = (e: InputChangeEvent) => {
    const inputValue = e.target.value
    const itemsPerPageValue = parseInt(inputValue)

    if (inputValue === '') {
      return
    }

    if (isNaN(itemsPerPageValue) || itemsPerPageValue < 5) {
      e.target.value = '5'
    } else if (itemsPerPageValue > 100) {
      e.target.value = '100'
    }

    if (changeItemsPerPage) {
      changeItemsPerPage(e)
    }
  }

  const handleItemsPerPageBlur = (e: InputChangeEvent) => {
    const inputValue = e.target.value
    const itemsPerPageValue = parseInt(inputValue)
    const minAllowed = 5
    const maxAllowed = 100

    if (inputValue === '' || inputValue === '0' || isNaN(itemsPerPageValue)) {
      const defaultValue =
        maxItemsPerPage && maxItemsPerPage >= minAllowed && maxItemsPerPage <= maxAllowed
          ? maxItemsPerPage
          : 20
      e.target.value = defaultValue.toString()
      if (changeItemsPerPage) {
        changeItemsPerPage(e)
      }
    } else if (itemsPerPageValue < minAllowed) {
      e.target.value = minAllowed.toString()
      if (changeItemsPerPage) {
        changeItemsPerPage(e)
      }
    } else if (itemsPerPageValue > maxAllowed) {
      e.target.value = maxAllowed.toString()
      if (changeItemsPerPage) {
        changeItemsPerPage(e)
      }
    }
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.nav)}>
        <Flexbox alignItems="center">
          <Text small aria-hidden={!labelStart}>
            {labelStart && labelStart}
          </Text>

          {labelStart && (
            <>
              <Spacer x="mini" />
              <Input
                aria-hidden={!labelStart}
                aria-label="Items per page"
                size="mini"
                width="25%"
                max="100"
                min="1"
                type="number"
                value={pageSizeValue ?? maxItemsPerPage}
                onChange={changeItemsPerPage}
                onBlur={handleItemsPerPageBlur}
                error={pageSizeError}
                errorMsg={pageSizeErrorMsg}
              ></Input>
              <Spacer x="mini" />
            </>
          )}
          <Text small aria-hidden={!label}>
            {label && label}
          </Text>
        </Flexbox>
        <Flexbox alignItems="center">
          {!labelStart && (
            <>
              <Text small style={{ marginRight: '0.25rem' }}>
                Page {currentPageNumber}
              </Text>
            </>
          )}

          {labelStart && (
            <>
              <Text small>Page</Text>
              <Spacer x="mini" />
              <Input
                aria-label="Go to page"
                size="mini"
                width="25%"
                max={totalPages}
                min="1"
                type="number"
                value={currentPageNumber}
                onChange={handlePageInputChange}
                onBlur={handlePageInputBlur}
              ></Input>
              <Spacer x="mini" />
            </>
          )}

          <Text small>of {totalPages}</Text>

          <Spacer x="mini" />
          <div
            role="navigation"
            aria-label="Pagination"
            className={css(styles.list)}
            aria-hidden={totalPages < 2}
          >
            {totalPages > 1 && (
              <>
                <Button
                  onClick={changePage}
                  disabled={currentPageNumber === 1}
                  appearance="text"
                  size="compact"
                  aria-label="Go to previous page"
                  icon={<Icon name="ChevronLeft" />}
                  id={PAGINATION_PREV_BUTTON_ID}
                >
                  Previous
                </Button>

                <Spacer x="default" />

                <Button
                  onClick={changePage}
                  disabled={currentPageNumber === totalPages}
                  appearance="text"
                  size="compact"
                  aria-label="Go to next page"
                  iconRight={<Icon name="ChevronRight" />}
                  id={PAGINATION_NEXT_BUTTON_ID}
                >
                  Next
                </Button>
              </>
            )}
          </div>
        </Flexbox>
      </div>
    </div>
  )
}
export default Pagination
